import React, { useEffect, useState, useCallback } from 'react';
import { Button, Modal, Spin, message, Tooltip } from 'antd';
import { DeleteOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { attachmentExtensionsApi } from '../../services/api/attachmentExtensionsApi';
import { useCapability } from '../auth/RoleGuard';

export interface PhotoStripProps {
  entityType: string; entityId: number; subType?: string; editable?: boolean;
  /** Bump this to force a re-fetch (e.g. after a sibling CameraCaptureField
   *  uploads a new photo). PhotoStrip only fetches when its inputs change, so
   *  without this a freshly-uploaded photo wouldn't appear until a full reload. */
  reloadKey?: number;
}

// The attachments API returns `file_url` (e.g. "/uploads/attachments/uuid.png").
// `file_path` is also tolerated for any legacy/alternate payload shape.
interface Attachment {
  id: number;
  file_url?: string;
  file_path?: string;
  file_name?: string;
  visibility?: 'internal' | 'customer';
}

const PhotoStrip: React.FC<PhotoStripProps> = ({ entityType, entityId, subType, editable, reloadKey }) => {
  const [photos, setPhotos] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const canDelete = useCapability('delete_photos');

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await attachmentExtensionsApi.listByEntity(entityType, entityId, subType);
      setPhotos(rows);
    } finally { setLoading(false); }
  }, [entityType, entityId, subType, reloadKey]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const onDelete = async (id: number) => {
    try {
      await attachmentExtensionsApi.delete(id);
      setPhotos((p) => p.filter((x) => x.id !== id));
      message.success('Photo deleted');
    } catch (e: any) { message.error(`Delete failed: ${e.message}`); }
  };

  const onToggleVisibility = async (e: React.MouseEvent, photo: Attachment) => {
    e.stopPropagation();
    const next: 'internal' | 'customer' = photo.visibility === 'customer' ? 'internal' : 'customer';
    try {
      await attachmentExtensionsApi.setVisibility(photo.id, next);
      setPhotos((prev) =>
        prev.map((p) => (p.id === photo.id ? { ...p, visibility: next } : p))
      );
      message.success(next === 'customer' ? 'Visible to customer' : 'Internal only');
    } catch (e: any) { message.error(`Visibility update failed: ${e.message}`); }
  };

  if (loading) return <Spin />;
  if (photos.length === 0) return <div style={{ color: '#999' }}>No photos yet.</div>;

  const urlFor = (p: Attachment) => {
    const raw = p.file_url ?? p.file_path ?? '';
    if (!raw) return '';
    return `/${raw.replace(/^.*uploads/, 'uploads')}`;
  };

  return (
    <>
      <div style={{ display: 'flex', overflowX: 'auto', gap: 8, padding: 8 }}>
        {photos.map((p) => (
          <div key={p.id} style={{ position: 'relative', flex: '0 0 auto' }}>
            <img src={urlFor(p)} alt={p.file_name ?? `photo ${p.id}`}
              style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
              onClick={() => setPreviewId(p.id)} />
            {editable && canDelete && (
              <Button size="small" danger icon={<DeleteOutlined />}
                data-testid={`photo-delete-${p.id}`}
                style={{ position: 'absolute', top: 4, right: 4 }}
                onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} />
            )}
            {editable && (
              <Tooltip title={p.visibility === 'customer' ? 'Visible to customer' : 'Internal only'}>
                <Button
                  size="small"
                  icon={p.visibility === 'customer' ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  data-testid={`photo-visibility-${p.id}`}
                  style={{ position: 'absolute', bottom: 4, right: 4 }}
                  onClick={(e) => onToggleVisibility(e, p)}
                />
              </Tooltip>
            )}
          </div>
        ))}
      </div>
      <Modal
        open={previewId !== null}
        footer={null}
        onCancel={() => setPreviewId(null)}
        centered
        styles={{ body: { display: 'flex', justifyContent: 'center', alignItems: 'center' } }}
      >
        {previewId !== null && photos.find(p => p.id === previewId) && (
          <img
            src={urlFor(photos.find(p => p.id === previewId)!)}
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
            alt=""
          />
        )}
      </Modal>
    </>
  );
};
export default PhotoStrip;
